// src/admin/FormEditor/components/QuestionEditor.jsx
// Main component for editing individual form questions

import { TRANSLATIONS, getPresetMutexIds } from "../../../constants/locale";
import { Copy, GripVertical, Info, Plus, Trash2 } from "lucide-react";
import { memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CommonRegexPatterns,
  ENABLE_EXPERIMENTAL_FEATURE,
} from "../../../constants/common";
import {
  addOption,
  addQuestion,
  removeQuestion,
  reorderOptions,
  updateQuestion,
} from "../store/formEditorSlice";
import {
  createDefaultOption,
  createDefaultQuestion,
  generateObjectId,
} from "../utils/formTemplates";
import { sanitizeIdentifier } from "../utils/sanitization";
import { CollapsibleSection } from "./CollapsibleSection";
import { DraggableList } from "./DraggableList";
import OptionEditor from "./OptionEditor";
import { RichTextEditor } from "./RichTextEditor.jsx";

const TextAlignments = {
  LEFT: "left",
  CENTER: "center",
  RIGHT: "right",
  JUSTIFY: "justify",
};

// Constants for time presets
const TimePresetTypes = {
  HOURLY: "hourly",
  HALF_HOUR: "half_hour",
  QUARTER_HOUR: "quarter_hour",
  MINUTE: "minute",
};

// Constants for question types
const QuestionTypes = {
  LABEL: "label",
  TEXT: "text",
  TEXTAREA: "textarea",
  CHECKBOXGROUP: "checkboxgroup",
  CHECKBOX: "checkbox",
  RADIO: "radio",
  DROPDOWN: "dropdown",
  DATE: "date",
  TIME: "time",
  LIST_TEXT: "list_text",
  PHONE_NUMBER: "phone_number",
  LIST_SUBFORM: "list_subform",
};

const isMultiChoiceType = (type) => {
  return [
    QuestionTypes.CHECKBOXGROUP,
    QuestionTypes.DROPDOWN,
    QuestionTypes.RADIO,
  ].includes(type);
};

const isSequenceType = (type) => {
  return [QuestionTypes.LIST_TEXT].includes(type);
};

// Helper component for input fields
const LanguageInputField = memo(
  ({ questionIndex, lang, field, label, placeholder, isTextArea = false }) => {
    const dispatch = useDispatch();
    const languagesValue = useSelector(
      (state) => state.formEditor.formMetadata.questions[questionIndex][field]
    );

    const handleChange = (e) => {
      const newValue = { ...languagesValue };
      newValue[lang] = e.target.value === "" ? null : e.target.value;
      const updates = {};
      updates[field] = newValue;
      dispatch(updateQuestion({ questionIndex, updates }));
    };

    return (
      <div
        className={`flex ${isTextArea ? "items-start" : "items-center"} gap-2`}
      >
        <span className="w-48 text-sm text-gray-700">{label}</span>
        {isTextArea ? (
          <textarea
            value={languagesValue[lang] || ""}
            onChange={handleChange}
            className="flex-1 border rounded-md p-2"
            placeholder={placeholder}
            rows="2"
          />
        ) : (
          <input
            type="text"
            value={languagesValue[lang] || ""}
            onChange={handleChange}
            className="flex-1 border rounded-md p-2"
            placeholder={placeholder}
          />
        )}
      </div>
    );
  }
);

const PhoneNumberSettings = memo(
  ({ questionIndex, handleQuestionFieldChange }) => {
    const dispatch = useDispatch();
    const question = useSelector(
      (state) => state.formEditor.formMetadata.questions[questionIndex]
    );
    const validation = question.validation;

    const handleValidationChange = (field, value) => {
      const newValidation = { ...validation };
      newValidation[field] = value;
      const updates = {};
      updates.validation = newValidation;
      dispatch(updateQuestion({ questionIndex, updates }));
    };

    const handleRegexChange = (changes) => {
      const newValidation = { ...validation };
      newValidation.regex = { ...newValidation.regex, ...changes };
      const updates = {};
      updates.validation = newValidation;
      dispatch(updateQuestion({ questionIndex, updates }));
    };

    return (
      <div className="mt-4 space-y-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Min Length:</span>
            <input
              type="number"
              field="min_length"
              min="0"
              value={validation?.min_length ?? ""}
              onChange={(e) => {
                handleValidationChange(
                  "min_length",
                  e.target.value === "" ? null : parseInt(e.target.value)
                );
              }}
              className="w-20 p-2 border rounded-md"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Max Length:</span>
            <input
              type="number"
              min="0"
              value={validation?.max_length ?? ""}
              onChange={(e) => {
                handleValidationChange(
                  "max_length",
                  e.target.value === "" ? null : parseInt(e.target.value)
                );
              }}
              className="w-20 p-2 border rounded-md"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                field="with_areacode"
                checked={question?.with_areacode ?? false}
                onChange={(e) => {
                  handleQuestionFieldChange(e);

                  const with_areacode = e.target.checked;

                  const pattern = CommonRegexPatterns.find(
                    (p) =>
                      p.label ===
                      (with_areacode
                        ? "Phone Number with Areacode"
                        : "Phone Number")
                  );
                  if (pattern) {
                    handleRegexChange({
                      pattern: pattern.value,
                      example: pattern.example,
                    });
                  }
                }}
                className="text-blue-600"
              />
              With Area Code
            </label>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-20 text-sm text-gray-700">Pattern:</span>
              <input
                type="text"
                value={validation?.regex?.pattern ?? ""}
                disabled
                className="w-full p-2 border rounded-md bg-gray-50 text-gray-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="w-20 text-sm text-gray-700">Example:</span>
              <input
                type="text"
                value={validation?.regex?.example || ""}
                disabled
                className="w-full p-2 border rounded-md bg-gray-50 text-gray-500"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

// Helper components for specific question types
const TextInputSettings = memo(
  ({ questionIndex, handleQuestionFieldChange }) => {
    const dispatch = useDispatch();
    const question = useSelector(
      (state) => state.formEditor.formMetadata.questions[questionIndex]
    );
    const validation = question.validation;

    const handleValidationChange = (field, value) => {
      const newValidation = { ...validation };
      newValidation[field] = value;
      const updates = {};
      updates.validation = newValidation;
      dispatch(updateQuestion({ questionIndex, updates }));
    };

    const handleRegexChange = (changes) => {
      const newValidation = { ...validation };
      newValidation.regex = { ...newValidation.regex, ...changes };
      const updates = {};
      updates.validation = newValidation;
      dispatch(updateQuestion({ questionIndex, updates }));
    };

    return (
      <div className="mt-4 space-y-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Min Length:</span>
            <input
              type="number"
              field="min_length"
              min="0"
              value={validation?.min_length ?? ""}
              onChange={(e) => {
                handleValidationChange(
                  "min_length",
                  e.target.value === "" ? null : parseInt(e.target.value)
                );
              }}
              className="w-20 p-2 border rounded-md"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Max Length:</span>
            <input
              type="number"
              min="0"
              value={validation?.max_length ?? ""}
              onChange={(e) => {
                handleValidationChange(
                  "max_length",
                  e.target.value === "" ? null : parseInt(e.target.value)
                );
              }}
              className="w-20 p-2 border rounded-md"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                field="regex"
                checked={validation?.regex?.pattern !== null}
                onChange={(e) => {
                  const pattern = e.target.checked ? "" : null;
                  const example = e.target.checked ? "" : null;
                  handleRegexChange({ pattern, example });
                }}
                className="text-blue-600"
              />
              Use Regex Validation
            </label>

            {question.validation?.regex?.pattern !== null && (
              <select
                value={question.validation.regex.pattern || ""}
                onChange={(e) => {
                  const pattern = CommonRegexPatterns.find(
                    (p) => p.value === e.target.value
                  );
                  if (pattern) {
                    handleRegexChange({
                      pattern: pattern.value,
                      example: pattern.example,
                    });
                  }
                }}
                className="flex-1 p-2 border rounded-md"
              >
                <option value="">Select a common pattern</option>
                {CommonRegexPatterns.map((pattern) => (
                  <option key={pattern.label} value={pattern.value}>
                    {pattern.label}
                  </option>
                ))}
              </select>
            )}
          </div>
          {validation?.regex?.pattern !== null && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-20 text-sm text-gray-700">Pattern:</span>
                <input
                  type="text"
                  value={validation.regex.pattern}
                  onChange={(e) =>
                    handleRegexChange({ pattern: e.target.value })
                  }
                  className="w-full p-2 border rounded-md"
                  placeholder="Enter regex pattern"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="w-20 text-sm text-gray-700">Example:</span>
                <input
                  type="text"
                  value={validation.regex.example}
                  onChange={(e) =>
                    handleRegexChange({ example: e.target.value })
                  }
                  className="w-full p-2 border rounded-md"
                  placeholder="Enter example that matches the pattern"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

// Settings component for TextArea
const TextAreaSettings = memo(
  ({ questionIndex, handleQuestionFieldChange }) => {
    const dispatch = useDispatch();
    const question = useSelector(
      (state) => state.formEditor.formMetadata.questions[questionIndex]
    );
    const validation = question.validation;

    const handleValidationChange = (field, value) => {
      const newValidation = { ...validation };
      newValidation[field] = value;
      const updates = {};
      updates.validation = newValidation;
      dispatch(updateQuestion({ questionIndex, updates }));
    };

    return (
      <div className="mt-4 space-y-4">
        {/* Character Length Controls */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Min Length:</span>
            <input
              type="number"
              field="min_length"
              min="0"
              value={validation?.min_length ?? ""}
              onChange={(e) =>
                handleValidationChange(
                  "min_length",
                  e.target.value === "" ? null : parseInt(e.target.value)
                )
              }
              className="w-20 p-2 border rounded-md"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Max Length:</span>
            <input
              type="number"
              field="max_length"
              min={validation?.max_length || 1}
              value={validation?.max_length ?? ""}
              onChange={(e) =>
                handleValidationChange(
                  "max_length",
                  e.target.value === "" ? null : parseInt(e.target.value)
                )
              }
              className="w-20 p-2 border rounded-md"
            />
          </div>
        </div>

        {/* Row Controls */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Min Rows:</span>
            <input
              type="number"
              field="min_rows"
              min="1"
              max={validation?.max_rows}
              value={validation?.min_rows ?? ""}
              onChange={(e) => {
                handleValidationChange(
                  "min_rows",
                  e.target.value === "" ? null : parseInt(e.target.value)
                );
              }}
              className="w-20 p-2 border rounded-md"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Max Rows:</span>
            <input
              type="number"
              field="max_rows"
              min={validation?.min_rows || 1}
              value={validation?.max_rows ?? ""}
              onChange={(e) => {
                handleValidationChange(
                  "max_rows",
                  e.target.value === "" ? null : parseInt(e.target.value)
                );
              }}
              className="w-20 p-2 border rounded-md"
            />
          </div>
        </div>

        {/* Default Rows Control */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Default Number of Rows:</span>
          <input
            type="number"
            field="default_rows"
            min="1"
            value={question.default_rows ?? 3}
            onChange={handleQuestionFieldChange}
            className="w-20 p-2 border rounded-md"
          />
        </div>

        {/* Resizable Control */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              field="resizable"
              checked={question.resizable ?? true}
              onChange={handleQuestionFieldChange}
              className="w-4 h-4 text-blue-600"
            />
            <span className="text-sm text-gray-700">Resizable</span>
          </label>
        </div>
      </div>
    );
  }
);

const TimeInputSettings = memo(
  ({ questionIndex, handleQuestionFieldChange }) => {
    const question = useSelector(
      (state) => state.formEditor.formMetadata.questions[questionIndex]
    );

    return (
      <div className="mt-4">
        <label className="flex items-center">
          <span className="w-48 text-sm text-gray-700">Time Precision</span>
          <select
            field="timePreset"
            value={TimePresetTypes.HOURLY}
            onChange={handleQuestionFieldChange}
            disabled={true}
            className="flex-1 p-2 border rounded-md bg-gray-100 cursor-not-allowed"
          >
            <option value={TimePresetTypes.HOURLY}>
              Hourly (00:00, 01:00, ...)
            </option>
            <option value={TimePresetTypes.HALF_HOUR}>
              Half Hour (00:00, 00:30, ...)
            </option>
            <option value={TimePresetTypes.QUARTER_HOUR}>
              Quarter Hour (00:00, 00:15, ...)
            </option>
            <option value={TimePresetTypes.MINUTE}>Custom Minutes</option>
          </select>
        </label>
      </div>
    );
  }
);

const MultiChoiceSettings = memo(({ questionIndex, withMutexSetting }) => {
  const dispatch = useDispatch();
  const question = useSelector(
    (state) => state.formEditor.formMetadata.questions[questionIndex]
  );
  const options = question.options;
  const mutexId = question.mutex_id;

  const handleOptionsDisplayInlineChange = (e) => {
    const updates = { options_display_inline: e.target.checked };
    dispatch(updateQuestion({ questionIndex, updates }));
  };

  const handleAddOption = () => {
    dispatch(addOption({ questionIndex }));
  };

  const handleReorderOptions = (newOrder) => {
    dispatch(reorderOptions({ questionIndex, newOrder }));
  };

  const handleMutexIdChange = (e) => {
    const updates = {};
    updates.mutex_id = e.target.value;
    dispatch(updateQuestion({ questionIndex, updates }));
  };

  // Check if the question type supports inline display
  const supportsInlineDisplay = [
    QuestionTypes.RADIO,
    QuestionTypes.CHECKBOXGROUP,
  ].includes(question.type);

  return (
    <div className="mt-4 space-y-4">
      {/* Only show inline display option for radio and checkbox groups */}
      {ENABLE_EXPERIMENTAL_FEATURE && supportsInlineDisplay && (
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={question.options_display_inline || false}
              onChange={handleOptionsDisplayInlineChange}
              className="text-blue-600 rounded"
            />
            Options display inline
          </label>
        </div>
      )}

      {withMutexSetting && (
        <div className="flex items-center gap-2">
          <span className="w-48 text-sm text-gray-700">Mutex Group ID</span>
          <input
            type="text"
            value={mutexId || ""}
            onChange={handleMutexIdChange}
            placeholder="Enter mutex group ID"
            className="flex-1 p-2 border rounded-md"
          />
        </div>
      )}

      {withMutexSetting && mutexId && (
        <div className="rounded-md bg-blue-50 p-3">
          <div className="flex items-start gap-2">
            <div className="flex-shrink-0 mt-0.5">
              <Info size={16} className="text-blue-500" />
            </div>
            <div className="text-sm">
              <p className="font-medium text-blue-800">Mutex Group Behavior:</p>
              <ul className="mt-1 list-disc list-inside space-y-1 text-gray-700">
                <li>Questions with the same mutex ID are linked together</li>
                <li>
                  The order of questions determines their precedence (earlier
                  questions have higher priority)
                </li>
                <li className="text-indigo-700 font-medium">
                  If a dominating option is selected in a higher priority
                  question, it will:
                </li>
                <ul className="ml-4 list-disc list-inside text-indigo-600">
                  <li>
                    Disable all lower priority questions in the same mutex group
                  </li>
                  <li>Force the same value in all lower priority questions</li>
                </ul>
                <li className="text-emerald-700 font-medium mt-2">
                  To customize mutex conflict warning message:
                </li>
                <ul className="ml-4 list-disc list-inside text-emerald-600">
                  <li>
                    Check locale.js for the validation.mutex.conflict node
                  </li>
                  <li>Add your mutex_id under the conflict object</li>
                  <li>Rebuild is required if different message is used</li>
                </ul>
                {getPresetMutexIds().length > 0 && (
                  <>
                    <li className="text-purple-700 font-medium mt-2">
                      Current preset mutex IDs with special warnings:
                    </li>
                    <ul className="ml-4 list-disc list-inside text-purple-600">
                      {getPresetMutexIds().map(({ id, message }) => (
                        <li key={id}>
                          <span className="font-medium">{id}</span>: {message}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="text-sm text-gray-700">Options:</div>
      <DraggableList
        items={options}
        onReorder={handleReorderOptions}
        renderItem={(option, optionIndex) => (
          <OptionEditor
            key={option.object_id}
            questionIndex={questionIndex}
            optionIndex={optionIndex}
          />
        )}
      />
      <button
        onClick={handleAddOption}
        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg
                 text-gray-500 hover:border-blue-500 hover:text-blue-500
                 flex items-center justify-center gap-2"
      >
        <Plus size={16} />
        Add Option
      </button>
    </div>
  );
});

const CheckboxGroupSettings = memo(({ questionIndex }) => {
  const dispatch = useDispatch();
  const validation = useSelector(
    (state) => state.formEditor.formMetadata.questions[questionIndex].validation
  );

  const handleValidationChange = (e) => {
    const field = e.target.getAttribute("field");
    const newValidation = { ...validation };
    const updates = {};

    if (e.target.type === "checkbox") {
      newValidation[field] = e.target.checked;
    } else if (e.target.type === "number") {
      newValidation[field] =
        e.target.value === "" ? null : parseInt(e.target.value);
    } else {
      newValidation[field] = e.target.value === "" ? null : e.target.value;
    }
    updates.validation = newValidation;
    dispatch(updateQuestion({ questionIndex, updates }));
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Min Selections:</span>
          <input
            type="number"
            field="min_selections"
            min="0"
            value={validation?.min_selections ?? ""}
            onChange={handleValidationChange}
            className="w-20 border rounded-md p-2"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Max Selections:</span>
          <input
            type="number"
            field="max_selections"
            min="1"
            value={validation?.max_selections ?? ""}
            onChange={handleValidationChange}
            className="w-20 border rounded-md p-2"
          />
        </div>
      </div>
      <MultiChoiceSettings
        questionIndex={questionIndex}
        withMutexSetting={false}
      />
    </div>
  );
});

const ListTextSettings = memo(
  ({ questionIndex, handleQuestionFieldChange }) => {
    const dispatch = useDispatch();
    const question = useSelector(
      (state) => state.formEditor.formMetadata.questions[questionIndex]
    );
    const supported_languages = useSelector(
      (state) => state.formEditor.formMetadata.supported_languages
    );
    const validation = question.validation;
    const listCountMessagesReadme = `(Use {max_count} to show the maximum allowed count. For example: "You can input {max_count} participant names by clicking +")`;

    const handleValidationChange = (field, value) => {
      const newValidation = { ...validation };
      newValidation[field] = value;
      const updates = {};
      updates.validation = newValidation;
      dispatch(updateQuestion({ questionIndex, updates }));
    };

    const handleRegexChange = (changes) => {
      const newValidation = { ...validation };
      newValidation.regex = { ...newValidation.regex, ...changes };
      const updates = {};
      updates.validation = newValidation;
      dispatch(updateQuestion({ questionIndex, updates }));
    };

    const handleMaxCountChange = (value) => {
      const updates = {
        max_count: value === "" ? null : parseInt(value),
      };
      dispatch(updateQuestion({ questionIndex, updates }));
    };

    const handleCountNoticeChange = (lang, value) => {
      const updates = {
        count_notice: {
          ...(question.count_notice || {}),
          [lang]: value,
        },
      };
      dispatch(updateQuestion({ questionIndex, updates }));
    };

    return (
      <div className="mt-4 space-y-4">
        {/* List Size Control */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Maximum List Size:</span>
          <input
            type="number"
            min="1"
            value={question.max_count ?? ""}
            onChange={(e) => handleMaxCountChange(e.target.value)}
            className="w-20 p-2 border rounded-md"
          />
        </div>

        {/* Count Notice Messages */}
        <div className="space-y-2">
          <div className="text-sm text-gray-700 mb-1">
            Count Notice Messages: {listCountMessagesReadme}
            <span className="ml-2 text-xs text-gray-500"></span>
          </div>
          {supported_languages.map((lang) => (
            <div key={lang} className="flex items-center gap-2">
              <span className="w-20 text-sm text-gray-700">[{lang}]</span>
              <input
                type="text"
                value={question.count_notice?.[lang] || ""}
                onChange={(e) => handleCountNoticeChange(lang, e.target.value)}
                className="flex-1 p-2 border rounded-md"
                placeholder={`Enter count notice message in ${lang}`}
              />
            </div>
          ))}
        </div>

        {/* Expand rows Control */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={question.expand_rows_in_report}
              onChange={(e) => {
                const updates = {
                  expand_rows_in_report: e.target.checked,
                };
                dispatch(updateQuestion({ questionIndex, updates }));
              }}
              className="text-blue-600 rounded"
            />
            Expand rows in report
            <span className="ml-1 text-xs text-gray-500">
              (Each item will occupy one row in the exported report)
            </span>
          </label>
        </div>

        {/* Text Input Controls */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Min Length:</span>
            <input
              type="number"
              min="0"
              value={validation?.min_length ?? ""}
              onChange={(e) =>
                handleValidationChange(
                  "min_length",
                  e.target.value === "" ? null : parseInt(e.target.value)
                )
              }
              className="w-20 p-2 border rounded-md"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Max Length:</span>
            <input
              type="number"
              min="0"
              value={validation?.max_length ?? ""}
              onChange={(e) =>
                handleValidationChange(
                  "max_length",
                  e.target.value === "" ? null : parseInt(e.target.value)
                )
              }
              className="w-20 p-2 border rounded-md"
            />
          </div>
        </div>

        {/* Regex Validation */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={validation?.regex?.pattern !== null}
                onChange={(e) => {
                  const pattern = e.target.checked ? "" : null;
                  const example = e.target.checked ? "" : null;
                  handleRegexChange({ pattern, example });
                }}
                className="text-blue-600"
              />
              Use Regex Validation
            </label>

            {validation?.regex?.pattern !== null && (
              <select
                value={validation.regex.pattern || ""}
                onChange={(e) => {
                  const pattern = CommonRegexPatterns.find(
                    (p) => p.value === e.target.value
                  );
                  if (pattern) {
                    handleRegexChange({
                      pattern: pattern.value,
                      example: pattern.example,
                    });
                  }
                }}
                className="flex-1 p-2 border rounded-md"
              >
                <option value="">Select a common pattern</option>
                {CommonRegexPatterns.map((pattern) => (
                  <option key={pattern.label} value={pattern.value}>
                    {pattern.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {validation?.regex?.pattern !== null && (
            <>
              <input
                type="text"
                value={validation.regex.pattern}
                onChange={(e) => handleRegexChange({ pattern: e.target.value })}
                className="w-full p-2 border rounded-md"
                placeholder="Enter regex pattern"
              />
              <input
                type="text"
                value={validation.regex.example}
                onChange={(e) => handleRegexChange({ example: e.target.value })}
                className="w-full p-2 border rounded-md"
                placeholder="Enter example that matches the pattern"
              />
            </>
          )}
        </div>
      </div>
    );
  }
);

const ListSubformSettings = memo(
  ({ questionIndex, handleQuestionFieldChange }) => {
    const dispatch = useDispatch();
    const question = useSelector(
      (state) => state.formEditor.formMetadata.questions[questionIndex]
    );
    const supported_languages = useSelector(
      (state) => state.formEditor.formMetadata.supported_languages
    );

    const handleMaxCountChange = (value) => {
      const updates = {
        max_count: value === "" ? null : parseInt(value),
      };
      dispatch(updateQuestion({ questionIndex, updates }));
    };

    const handleSubformIdChange = (value) => {
      const updates = {
        subform_id: value,
      };
      dispatch(updateQuestion({ questionIndex, updates }));
    };

    const handleCountNoticeChange = (lang, value) => {
      const updates = {
        count_notice: {
          ...(question.count_notice || {}),
          [lang]: value,
        },
      };
      dispatch(updateQuestion({ questionIndex, updates }));
    };

    return (
      <div className="mt-4 space-y-4">
        {/* Subform ID */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Subform ID:</span>
          <input
            type="text"
            value={question.subform_id ?? ""}
            onChange={(e) => handleSubformIdChange(e.target.value)}
            className="flex-1 p-2 border rounded-md"
            placeholder="Enter subform ID"
          />
        </div>

        {/* List Size Control */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Maximum List Size:</span>
          <input
            type="number"
            min="1"
            value={question.max_count ?? ""}
            onChange={(e) => handleMaxCountChange(e.target.value)}
            className="w-20 p-2 border rounded-md"
          />
        </div>

        {/* Count Notice Messages */}
        <div className="space-y-2">
          <div className="text-sm text-gray-700 mb-1">
            Count Notice Messages:
            <span className="ml-2 text-xs text-gray-500">
              (Use {"{max_count}"} to show the maximum allowed count)
            </span>
          </div>
          {supported_languages.map((lang) => (
            <div key={lang} className="flex items-center gap-2">
              <span className="w-20 text-sm text-gray-700">[{lang}]</span>
              <input
                type="text"
                value={question.count_notice?.[lang] || ""}
                onChange={(e) => handleCountNoticeChange(lang, e.target.value)}
                className="flex-1 p-2 border rounded-md"
                placeholder={`Enter count notice message in ${lang}`}
              />
            </div>
          ))}
        </div>

        {/* Expand rows Control */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={question.expand_rows_in_report}
              onChange={(e) => {
                const updates = {
                  expand_rows_in_report: e.target.checked,
                };
                dispatch(updateQuestion({ questionIndex, updates }));
              }}
              className="text-blue-600 rounded"
            />
            Expand rows in report
            <span className="ml-1 text-xs text-gray-500">
              (Each subform will occupy one row in the exported report)
            </span>
          </label>
        </div>
      </div>
    );
  }
);

// Main QuestionEditor component
const QuestionEditor = ({ questionIndex }) => {
  const dispatch = useDispatch();
  const supported_languages = useSelector(
    (state) => state.formEditor.formMetadata.supported_languages
  );
  const purpose = useSelector((state) => state.formEditor.formMetadata.purpose);

  const defaultQuestion = createDefaultQuestion(supported_languages, purpose);
  const question = useSelector(
    (state) => state.formEditor.formMetadata.questions[questionIndex]
  );

  const handleRemove = () => {
    dispatch(removeQuestion({ questionIndex }));
  };

  const handleFieldNameChange = (e) => {
    const sanitizedFieldName = sanitizeIdentifier(e.target.value);
    const updates = { field_name: sanitizedFieldName };
    dispatch(updateQuestion({ questionIndex, updates }));
  };

  const handleQuestionFieldChange = (e) => {
    const field = e.target.getAttribute("field");
    const updates = {};
    if (e.target.type === "checkbox") {
      updates[field] = e.target.checked;
    } else if (e.target.type === "number") {
      updates[field] = e.target.value === "" ? null : parseInt(e.target.value);
    } else {
      updates[field] = e.target.value;
    }
    dispatch(updateQuestion({ questionIndex, updates }));
  };

  const handleCopyQuestion = (e) => {
    // Deep copy the question
    const questionCopy = JSON.parse(JSON.stringify(question));

    // Generate new object_id and use it as field_name
    const newObjectId = generateObjectId("q_");

    questionCopy.object_id = newObjectId;
    questionCopy.field_name = newObjectId;

    // Generate new object_ids for options if they exist
    if (questionCopy.options) {
      questionCopy.options = questionCopy.options.map((option) => ({
        ...option,
        object_id: `option_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`,
      }));
    }

    dispatch(addQuestion({ question: questionCopy }));
  };

  const handleTypeChange = (newType) => {
    const updates = { ...question, type: newType };
    if (newType === QuestionTypes.LABEL) {
    }
    if (isMultiChoiceType(newType) && !question.options?.length) {
      updates.options = [createDefaultOption(supported_languages)];
    }
    if (newType === QuestionTypes.TIME) {
      updates.timePreset = question.timePreset ?? TimePresetTypes.MINUTE;
    } else if (newType === QuestionTypes.RADIO) {
      updates.required = true;
    } else if (newType === QuestionTypes.PHONE_NUMBER) {
      const with_areacode = question.with_areacode ?? false;
      const pattern = CommonRegexPatterns.find(
        (p) =>
          p.label ===
          (with_areacode ? "Phone Number with Areacode" : "Phone Number")
      );

      updates.validation = {
        min_length: 8,
        max_length: 20,
        regex: { pattern: pattern?.value, example: "" },
      };
    } else if (newType === QuestionTypes.TEXTAREA) {
      updates.validation = {
        min_length: null,
        max_length: 260,
        max_rows: null,
        regex: { pattern: "", example: "" },
      };
    } else if (newType === QuestionTypes.TEXT) {
      updates.validation = {
        min_length: null,
        max_length: 100,
        regex: { pattern: "", example: "" },
      };
    }
    dispatch(updateQuestion({ questionIndex, updates }));
  };

  const renderTypeSpecificSettings = () => {
    switch (question.type) {
      case QuestionTypes.TEXT:
        return (
          <TextInputSettings
            questionIndex={questionIndex}
            handleQuestionFieldChange={handleQuestionFieldChange}
          />
        );
      case QuestionTypes.TEXTAREA:
        return (
          <TextAreaSettings
            questionIndex={questionIndex}
            handleQuestionFieldChange={handleQuestionFieldChange}
          />
        );
      case QuestionTypes.TIME:
        return (
          <TimeInputSettings
            questionIndex={questionIndex}
            handleQuestionFieldChange={handleQuestionFieldChange}
          />
        );
      case QuestionTypes.CHECKBOXGROUP:
        return <CheckboxGroupSettings questionIndex={questionIndex} />;

      case QuestionTypes.RADIO:
      case QuestionTypes.DROPDOWN:
        return (
          <MultiChoiceSettings
            questionIndex={questionIndex}
            withMutexSetting={true}
          />
        );
      case QuestionTypes.LIST_TEXT:
        return (
          <ListTextSettings
            questionIndex={questionIndex}
            handleQuestionFieldChange={handleQuestionFieldChange}
          />
        );
      case QuestionTypes.PHONE_NUMBER: // Add this case
        return (
          <PhoneNumberSettings
            questionIndex={questionIndex}
            handleQuestionFieldChange={handleQuestionFieldChange}
          />
        );
      case QuestionTypes.LIST_SUBFORM:
        return (
          <ListSubformSettings
            questionIndex={questionIndex}
            handleQuestionFieldChange={handleQuestionFieldChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <CollapsibleSection
      id={`question-${questionIndex}`}
      title={
        <div className="flex items-center gap-4 w-full">
          <div className="flex items-center gap-2">
            <span className="w-6 text-sm font-medium text-gray-500">
              #{questionIndex}
            </span>
            <div className="cursor-grab">
              <GripVertical className="text-gray-400" size={20} />
            </div>
          </div>
          <div className="flex-1">
            <span className="font-medium">
              {question.field_name || `Question ${questionIndex + 1}`}
            </span>
            <span className="ml-2 text-sm text-gray-500">
              ({question.type || defaultQuestion.type})
            </span>
          </div>
          <div className="flex gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCopyQuestion();
              }}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Clone Question"
            >
              <Copy size={18} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              className="p-2 text-gray-400 hover:text-red-500 transition-colors"
              title="Delete Question"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      }
      bgColor="bg-gray-50"
      borderColor="border-gray-200"
      summary={
        <div className="text-sm text-gray-600">
          {supported_languages.map((lang) => (
            <span key={lang} className="mr-4">
              [{lang}] {question.title[lang] || "No title"}
            </span>
          ))}
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 flex items-center gap-2">
            <span className="w-48 text-sm text-gray-700">Field Name</span>
            <input
              type="text"
              value={question.field_name}
              onChange={handleFieldNameChange}
              className="flex-1 p-2 border rounded-md"
              placeholder="e.g., first_name, email"
            />
          </div>

          <div className="flex items-center gap-4">
            <select
              value={
                question.type || defaultQuestion.type || QuestionTypes.TEXT
              }
              onChange={(e) => handleTypeChange(e.target.value)}
              className="p-2 border rounded-md bg-white"
            >
              <option value={QuestionTypes.LABEL}>Label</option>
              <option value={QuestionTypes.TEXT}>Text Input</option>
              <option value={QuestionTypes.TEXTAREA}>Text Area</option>
              <option value={QuestionTypes.CHECKBOX}>Single Checkbox</option>
              <option value={QuestionTypes.CHECKBOXGROUP}>
                Multiple Checkboxes
              </option>
              <option value={QuestionTypes.RADIO}>Radio Button</option>
              <option value={QuestionTypes.DROPDOWN}>Dropdown</option>
              <option value={QuestionTypes.DATE}>Date Picker</option>
              <option value={QuestionTypes.TIME}>Time Picker</option>
              <option value={QuestionTypes.LIST_TEXT}>
                List of Text Inputs
              </option>
              <option value={QuestionTypes.PHONE_NUMBER}>Phone Number</option>
              <option value={QuestionTypes.LIST_SUBFORM}>
                List of Subforms
              </option>
            </select>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={question.required || defaultQuestion.required}
                field="required"
                onChange={handleQuestionFieldChange}
                disabled={question.type === QuestionTypes.LABEL}
                className="w-4 h-4 text-blue-600 disabled:opacity-50"
              />
              Required
            </label>
          </div>
        </div>

        {/* Custom Report Header Row */}
        <div className="flex items-center gap-2">
          <span className="w-48 text-sm text-gray-700">
            Custom Report Header
          </span>
          <input
            type="text"
            value={question.custom_report_header || ""}
            field="custom_report_header"
            onChange={handleQuestionFieldChange}
            className="flex-1 p-2 border rounded-md"
            placeholder="Leave empty to use Title[en] as report header"
          />
        </div>

        {question.type === QuestionTypes.LABEL && (
          <>
            {/* Title Alignment */}
            <div className="flex items-center gap-2">
              <span className="w-48 text-sm text-gray-700">
                Title Alignment
              </span>
              <div className="flex gap-4">
                {Object.entries(TextAlignments).map(([key, value]) => (
                  <label key={value} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`alignment_${questionIndex}`}
                      value={value}
                      checked={question.title_align === value}
                      onChange={(e) => {
                        const updates = { title_align: e.target.value };
                        dispatch(updateQuestion({ questionIndex, updates }));
                      }}
                      className="text-blue-600"
                    />
                    <span className="text-sm text-gray-700">{key}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Enable RTF Title Checkbox */}
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={question.enable_rtf_title || false}
                  field="enable_rtf_title"
                  onChange={handleQuestionFieldChange}
                  className="text-blue-600 rounded"
                />
                Enable Rich Text Format Title
              </label>

              <span className="text-xs text-blue-600 italic">
                (RTF title will override regular title when enabled)
              </span>
            </div>

            {/* RTF Title Fields - only show if enable_rtf_title is true */}
            {question.enable_rtf_title && (
              <div className="space-y-4">
                {supported_languages.map((lang) => (
                  <div key={lang} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-700">
                        RTF Title [{lang.toUpperCase()}]
                      </div>
                      <div className="text-xs text-gray-500">
                        {question.rtf_title?.[lang]?.length || 0} characters
                      </div>
                    </div>
                    <RichTextEditor
                      value={question.rtf_title?.[lang] || ""}
                      onChange={(content) => {
                        const updates = {
                          rtf_title: {
                            ...(question.rtf_title || {}),
                            [lang]: content,
                          },
                        };
                        dispatch(updateQuestion({ questionIndex, updates }));
                      }}
                      language={lang}
                      fieldName={`rtf_title_${lang}`}
                      collapsible={true}
                      defaultCollapsed={false}
                      title={`RTF Title (${lang.toUpperCase()})`}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {!question.enable_rtf_title &&
          supported_languages.map((lang) => (
            <LanguageInputField
              key={`title-${lang}`}
              questionIndex={questionIndex}
              lang={lang}
              field="title"
              label={`Title [${lang}]`}
              placeholder={`Question title in ${lang}`}
            />
          ))}

        {supported_languages.map((lang) => (
          <LanguageInputField
            key={`desc-${lang}`}
            questionIndex={questionIndex}
            lang={lang}
            field="description"
            label={`Description [${lang}]`}
            placeholder={`Question description in ${lang}`}
          />
        ))}

        {/* always_display_answer_new_line */}
        {ENABLE_EXPERIMENTAL_FEATURE && (
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={question.always_display_answer_new_line || false}
                field="always_display_answer_new_line"
                onChange={handleQuestionFieldChange}
                className="text-blue-600 rounded"
              />
              Display answer in new line
              <span className="ml-1 text-xs text-gray-500">
                (Question and answer will be displayed in different lines)
              </span>
            </label>
          </div>
        )}

        {question.required && (
          <div className="space-y-2">
            <div className="text-sm text-gray-700 mb-1">
              Custom Mandatory Warning Messages:
              <span className="ml-2 text-xs text-gray-500">
                (Leave empty to use default warning message)
              </span>
            </div>
            {supported_languages.map((lang) => (
              <LanguageInputField
                key={`warning-${lang}`}
                questionIndex={questionIndex}
                lang={lang}
                field="mandatory_warning"
                label={`Warning [${lang}]`}
                placeholder={`Enter custom warning message in ${lang}`}
              />
            ))}
          </div>
        )}

        {renderTypeSpecificSettings()}
      </div>
    </CollapsibleSection>
  );
};

export default QuestionEditor;
